import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, Loader2, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/assets";

export default function UpdateAddress() {
  const { token } = useParams();
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ address_street: "", address_suburb: "", address_postcode: "" });

  useEffect(() => {
    api.get(`/members/update/${token}`)
      .then(({ data }) => {
        setMember(data);
        setForm({
          address_street: data.address_street || "",
          address_suburb: data.address_suburb || "",
          address_postcode: data.address_postcode || "",
        });
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is not valid"));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.address_street.trim() && form.address_suburb.trim() && form.address_postcode.trim();

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/members/update/${token}`, form);
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

  return (
    <main data-testid="update-address-page" className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md border border-clay-border bg-clay-card p-8 sm:p-10">
        <img src={LOGO_URL} alt="Mid Western Speedway Darlington" className="h-16 mb-6" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2">Mid Western Speedway Darlington</p>

        {error ? (
          <div data-testid="update-address-error">
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">Link not valid</h1>
            <p className="text-clay-muted text-sm leading-relaxed">
              This update link is not valid or has expired. Please contact the club or reply to the email you received and we'll update your details for you.
            </p>
          </div>
        ) : done ? (
          <div data-testid="update-address-done" className="text-center py-6">
            <BadgeCheck className="w-14 h-14 text-clay-gold mx-auto mb-4" />
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">All updated!</h1>
            <p className="text-clay-muted text-sm leading-relaxed">
              Thanks {member?.name?.split(" ")[0]} — your mailing address has been updated. See you at the track.
            </p>
          </div>
        ) : member ? (
          <>
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-1">Update Your Address</h1>
            <p className="text-clay-muted text-sm mb-6">
              Hi {member.name?.split(" ")[0]} — check your mailing address below and save any changes.
            </p>
            <div className="space-y-4">
              <input data-testid="update-address-street-input" placeholder="Street address *" value={form.address_street} onChange={set("address_street")} className={inputCls} />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="update-address-suburb-input" placeholder="Suburb / Town *" value={form.address_suburb} onChange={set("address_suburb")} className={inputCls} />
                <input data-testid="update-address-postcode-input" placeholder="Postcode *" inputMode="numeric" value={form.address_postcode} onChange={set("address_postcode")} className={inputCls} />
              </div>
              <button
                data-testid="update-address-save-button"
                onClick={save}
                disabled={busy || !valid}
                className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                {busy ? "Saving…" : "Save My Address"}
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-center">
                Or just reply to our email with your new address
              </p>
            </div>
          </>
        ) : (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-clay-orange" />
          </div>
        )}
      </div>
    </main>
  );
}
